import type { ServiceRecordListQuery } from '@chukua/common';
import { Select, Table } from 'antd';
import { ClipboardList, SearchX } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { ServiceRecordView, TruckServiceItemView } from '../truck-service.api';
import { buildServiceRecordColumns } from './build-service-record-columns';

interface ServiceRecordsTableProps {
  items: ServiceRecordView[];
  total: number;
  serviceTypes: TruckServiceItemView[];
  list: ListQueryControls<ServiceRecordListQuery>;
  isRefreshing: boolean;
}

/** Search, a type-of-service filter and the server-paged history of a truck's services; the old rows stay up, dimmed, while the next page loads. */
export function ServiceRecordsTable({ items, total, serviceTypes, list, isRefreshing }: ServiceRecordsTableProps) {
  if (total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No service recorded yet"
        description="Add an entry when a service is done, or to enter the values from before this system."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <ListSearch value={list.query.q} placeholder="Search by service" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <Select
          mode="multiple"
          allowClear
          className="min-w-64"
          placeholder="Type of service"
          optionFilterProp="label"
          value={list.query.serviceItem ?? []}
          options={serviceTypes.map((type) => ({ value: type.serviceItemId, label: type.name }))}
          onChange={(serviceItem) => list.setQuery({ serviceItem })}
        />
      </div>
      <Table
        rowKey="id"
        dataSource={items}
        columns={buildServiceRecordColumns({ list })}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={list.isFiltered ? { emptyText: <EmptyState icon={SearchX} title="No matching entries" description="No service entries match." /> } : undefined}
        {...list.tableProps<ServiceRecordView>(total)}
      />
    </div>
  );
}
